<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="search-facet-expand"
      dialog-width="90vw"
      dialog-height="90vh"
      dialog-max-width="90vw"
      dialog-max-height="90vh"
    >
      <template #header>{{ ls(def.name) }}</template>
      <template #header-extra>
        <UiInputRound class="jps-round-input search-facet-expand-header-extra" v-model="filter" />
      </template>
      <div class="jps-modal-content">
        <div class="search-facet-facets">
          <div
            class="search-facet-facet"
            :class="{ 'is-selected': isSelected(value) || isExclude(value) }"
            v-for="[value, count] in Array.from(sortedFacet)"
            :key="value"
          >
            <BaseCheckbox
              :model-value="isSelected(value) || isExclude(value)"
              @update:model-value="updateCheck(value)"
              :indeterminate="isExclude(value)"
              class="checkbox"
            >
              <SearchFacetValue
                class="value is-size-7-touch"
                :facet="facet"
                :def="def"
                :value="value"
              />
              <span v-if="!isExclude(value)" class="count is-size-7-touch">{{ count }}</span>
              <span v-if="_isFacetExclude && (isSelected(value) || isExclude(value))" class="padding" />
            </BaseCheckbox>
            <div
              v-if="_isFacetExclude && !isSelected(value) && !isExclude(value)"
              :title="lss('検索結果から除外します', 'Exclude from search result')"
              @click="exclude(value)"
              class="not-facet is-size-7-touch"
            >
              <BaseIcon
                arialabel="閉じる"
                name="mdi/close-circle-outline"
                size="size-4"
              />
            </div>
          </div>
        </div>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="modal.resolve(selected)">{{ lss('絞り込み', 'Narrow down') }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'
import { PREFECTURES } from '~/components/search/useSearchField'
import { useSearchFieldExpand } from '~/components/search/search-field/useSearchFieldExpand'
import type { SearchFieldExpandProps } from '~/components/search/search-field/useSearchFieldExpand'

const { ls, lss } = useI18n()
const { $getContentsCode, $getContentsCodeLang } = useNuxtApp()
const dataStore = useDataStore()

interface Props extends SearchFieldExpandProps {
  isFacetExclude?: boolean,
}
const props = withDefaults(defineProps<Props>(), {
  isFacetExclude: false,
})

const {
  init,
  selected,
  filter,
  code,
  select,
  exclude,
  isSelected,
  isExclude,
} = useSearchFieldExpand(props)

const contentsCode: string[] = $getContentsCode()
const contentsCodeLang: {} = $getContentsCodeLang()

const modal = ref<InstanceType<typeof BaseModal>>()
const _isFacetExclude: Ref<boolean> = ref(false)
const openAwait = async (options?: Props) => {
  setOptionsValue(_isFacetExclude, 'isFacetExclude', props, options, false)
  init(_isFacetExclude.value)
  return await modal.value.openAwait<string[]>()
}
defineExpose({ openAwait })

const sortedFacet = computed(() => {
  const obj = new Map()
  let keys = Object.keys(props.facet.counts).filter((key) => {
    let values = [key]
    switch (props.def.type) {
      case 'keyword':
        //stringの場合はkeyをそのままつかう。
        break
      case 'code':
        if (code.value) {
          let cv = code.value.values.find((v) => v.code === key)
          values.push(cv.value.ja, cv.value.en)
        }
        break
      case 'provider':
        const provider = dataStore.provider(key)
        if (provider) {
          values.push(provider.name.ja, provider.name.en)
        }
        break
      case 'organization':
        const org = dataStore.organization(key)
        if (org) {
          values.push(org.name.ja, org.name.en)
        }
        break
      case 'contents':
        values = []
        if (contentsCode.includes(key)) {
          values.push(key)
          values.push(contentsCodeLang[key].ja, contentsCodeLang[key].en)
        }
        break
      case 'tag':
        const tag = dataStore.tag(key)
        if (tag) {
          values.push(tag.label.ja, tag.label.en)
        }
        break
      case 'database':
        const db = dataStore.database(key)
        if (db) {
          values.push(db.name.ja, db.name.en)
        }
        break
    }

    if (values.length == 0) return false
    return includes(new RegExp(filter.value, 'i'), ...values)
  })

  if (props.def.type === 'code' && code.value) {
    if (props.def.codeKey === 'jps-language') {
      // 言語の場合は検索数が多い順、かつ、不明言語(und)は最後になるように並び替える
      keys = keys.sort((a, b) => props.facet.counts[b] - props.facet.counts[a])
      const undIndex = keys.indexOf('und')
      if (undIndex >= 0) {
        keys.splice(undIndex, 1)
        keys.push('und')
      }
    } else {
      keys = keys.sort(
        (a, b) =>
          code.value.values.findIndex((v) => v.code === a) -
          code.value.values.findIndex((v) => v.code === b)
      )
    }
  } else if (props.def.type === 'prefectures') {
    keys = PREFECTURES
        ?.map(prefecture => prefecture.key)
        ?.filter(key => props.facet.counts[key]) || []
  } else if (props.def.type === 'temporal_group') {
    keys = keys.sort((a, b) => Number(b) - Number(a))
  } else {
    keys = keys.sort((a, b) => props.facet.counts[b] - props.facet.counts[a])
  }
  keys.forEach((key) => {
    obj.set(key, props.facet.counts[key] || 0)
  })
  return obj
})

const updateCheck = (value: any) => {
  if (_isFacetExclude.value) {
    if (isSelected(value)) {
      select(value)
    } else if (isExclude(value)) {
      exclude(value)
    } else {
      select(value)
    }
  } else {
    select(value)
  }
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

/* scopedにすると、buefyのコンポーネントである.control-label以下のスタイルが崩れるため、non-scoped */

.search-facet-expand {
  /*   background: $jps-gray-100; */

  .facet-title {
    padding: 1rem 0.75rem 1rem 1rem;
    margin-bottom: 0;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    > .jps-icon {
      margin-right: 0rem;
    }
  }

  .facet-body {
    overflow-y: scroll;
  }

  .search-facet-facets {
    .search-facet-facet {
      padding: 0 0.75rem;
      width: 100%;
      display: flex;
      align-items: center;

      .jps-checkbox {
        margin-left: 0;
        width: 100%;
        .check {
          width: 1rem;
          height: 1rem;
        }
        .jps-checkbox-label {
          display: flex;
          align-items: center;
          line-height: 2.5rem;
          padding-left: 0.5rem;
          width: 100%;
          .value {
            flex-basis: 70%;
            line-height: 1.25rem;
            padding: 0.5rem 0rem;
            display: flex;
            align-items: center;
            i {
              margin: 0;
            }
            i + span {
              padding-left: 0.25rem;
            }
            span {
              @include typo-list();
            }
          }
          .count {
            flex-basis: 30%;
            text-align: center;
            a {
              float: right;
            }
          }
          .padding {
            width: 1.5rem;
            height: 2.5rem;
          }
        }
      }

      &:not(:last-child) {
        border-bottom: $basic-border;
      }
      /* &.is-selected {
        background: $jps-key-blue;
        color: $white;
        a {
          color: $white;
        }
      } */
      &.not-facet-selected {
        background: $jps-gray-100;
        color: $jps-gray-600;
        span {
          opacity: 0.65;
        }
        a {
          color: $jps-gray-600;
        }
      }

      .not-facet {
        i {
          cursor: pointer;
        }
        display: flex;
        align-items: center;
      }
    }
  }

  .show-remain-button {
    padding: 0.75rem;
    div {
      cursor: pointer;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      i {
        margin-right: 0.325rem;
        color: $jps-gray-800;
      }
    }
  }

  .not-facet-selected {
    > div {
      padding: 0 0.75rem;
      .checkbox {
        .check {
          width: 1rem;
          height: 1rem;
        }
      }
    }
    /*&:nth-child(3) {
        margin-top: 1rem;
      }*/
    &:not(:last-child) {
      border-bottom: $basic-border;
    }
    span.jps-checkbox-label {
      padding: 0.5rem;
    }
  }
}
.search-facet-expand-header-extra {
  background: $white !important;
  width: auto !important;
  margin-left: 1rem  !important;
  margin-right: 1rem  !important;
}
</style>
